import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { saveAs } from 'file-saver';
import get from 'lodash/get';
import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import {
  getApiErrorMessage,
  getBlobErrorMessage,
  UnwrapArray,
} from '@sbv-clients/util-misc';

import {
  Api,
  ReportCacheKeys,
  SaaSReportsReportApiSaaSReportsAvailableSchedulesGetRequest,
  SaaSReportsReportApiSaaSReportsDailyStaffingSheetGetRequest,
  SaaSReportsReportApiSaaSReportsMasterScheduleGetRequest,
  SaaSReportsReportApiSaaSReportsMonthlyPrintGetRequest,
  SaaSReportsReportApiSaaSReportsPrintDashboardGetRequest,
  SaaSReportsReportApiSaaSReportsPrintEmployeeViewGetRequest,
  SaaSReportsReportApiSaaSReportsPrintMasterSchedulePlannerGetRequest,
  SaveReportRequest,
  useApi,
} from '../index';

const reportsMonthlyPrintPost =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsMonthlyPrintGetRequest) => {
    return api.saaSReportsReportApi.saaSReportsMonthlyPrintGet(body, {
      responseType: 'blob',
    });
  };
export const useReportsMonthlyPrintPost = () => {
  const api = useApi();
  const { notifyError, notifyWarning } = useSnackBar();
  return useMutation(reportsMonthlyPrintPost(api), {
    onSuccess: async (response, requestBody) => {
      if (response.status === 204) {
        notifyWarning('Report is empty!');
        return;
      }

      const data = get(response, 'data', '') as string;
      const excelBlob = new Blob([data]);
      if (requestBody.format === 1) {
        saveAs(excelBlob, 'Monthly.xlsx');
      } else {
        saveAs(excelBlob, 'Monthly.pdf');
      }
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};
const reportsMasterPrintPost =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsMasterScheduleGetRequest) =>
    api.saaSReportsReportApi.saaSReportsMasterScheduleGet(body, {
      responseType: 'blob',
    });
export const useReportsMasterPrintPost = () => {
  const api = useApi();
  const { notifyError, notifyWarning } = useSnackBar();
  return useMutation(reportsMasterPrintPost(api), {
    onSuccess: async (response, requestBody) => {
      if (response.status === 204) {
        notifyWarning('Report is empty!');
        return;
      }

      const data = get(response, 'data', '') as string;
      const excelBlob = new Blob([data]);
      if (requestBody.format === 1) {
        saveAs(excelBlob, 'Master.xlsx');
      } else {
        saveAs(excelBlob, 'Master.pdf');
      }
    },
    onError: async (error) => {
      getBlobErrorMessage(error).then(notifyError);
    },
  });
};
const printDailyStaffingSheet =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsDailyStaffingSheetGetRequest) =>
    api.saaSReportsReportApi.saaSReportsDailyStaffingSheetGet(body, {
      responseType: 'blob',
    });
export const usePrintDailyStaffingSheet = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(printDailyStaffingSheet(api), {
    onSuccess: async (response, requestBody) => {
      const data = get(response, 'data', '') as string;
      const blob = new Blob([data]);
      if (requestBody.format === 1) {
        saveAs(blob, 'DailyStaffingSheet.xlsx');
      } else {
        saveAs(blob, 'DailyStaffingSheet.pdf');
      }
    },
    onError: async (error) => {
      getBlobErrorMessage(error).then(notifyError);
    },
  });
};
const reportsAvailableSchedulesPost =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsAvailableSchedulesGetRequest) =>
    api.saaSReportsReportApi.saaSReportsAvailableSchedulesGet(body, {
      responseType: 'blob',
    });
export const useReportsAvailableSchedulesPost = () => {
  const api = useApi();
  const { notifyError, notifyWarning } = useSnackBar();
  return useMutation(reportsAvailableSchedulesPost(api), {
    onSuccess: async (response, requestBody) => {
      if (response.status === 204) {
        notifyWarning('Report is empty!');
        return;
      }

      const data = get(response, 'data', '') as string;
      const excelBlob = new Blob([data]);
      if (requestBody.format === 1) {
        saveAs(excelBlob, 'Available Schedules.xlsx');
      } else {
        saveAs(excelBlob, 'Available Schedules.pdf');
      }
    },
    onError: async (error) => {
      getBlobErrorMessage(error).then(notifyError);
    },
  });
};

export const useSaveReportsGet = (reportName: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [ReportCacheKeys.SavedReports, { reportName }],
    queryFn: () =>
      api.reportsApi
        .reportsSavedReportsReportNameGet({
          reportName,
        })
        .then((response) => response?.data),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
type SaveReportsResponseType = NonNullable<
  ReturnType<typeof useSaveReportsGet>['data']
>;
export type SavedReportResponseType = NonNullable<
  UnwrapArray<SaveReportsResponseType['savedReports']>
>;
const saveReport = (api: Api) => (saveReportRequest: SaveReportRequest) => {
  return api.reportsApi.reportsSavedReportPost({ saveReportRequest });
};
export const useSaveReport = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const { notifyError, notifySuccess } = useSnackBar();
  return useMutation(saveReport(api), {
    onSuccess: async (response, requestBody) => {
      queryClient.invalidateQueries([ReportCacheKeys.SavedReports]).catch(noop);
      notifySuccess('Saved successfully');
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
const deleteSavedReport = (api: Api) => (savedReportId: number) => {
  return api.reportsApi.reportsSavedReportSavedReportIdDelete({
    savedReportId,
  });
};
export const useDeleteSavedReport = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();
  return useMutation(deleteSavedReport(api), {
    onSuccess: async () => {
      notifySuccess('Discarded successfully');
      queryClient.invalidateQueries([ReportCacheKeys.SavedReports]).catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
const printEmployeeReport =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsPrintEmployeeViewGetRequest) =>
    api.saaSReportsReportApi.saaSReportsPrintEmployeeViewGet(body, {
      responseType: 'blob',
    });
export const usePrintEmployeeReport = () => {
  const api = useApi();
  const { notifyError, notifyWarning } = useSnackBar();
  return useMutation(printEmployeeReport(api), {
    onSuccess: async (response) => {
      if (response.status === 204) {
        notifyWarning('Report is empty!');
        return;
      }
      const data = get(response, 'data', '') as string;
      const excelBlob = new Blob([data]);
      saveAs(excelBlob, 'Print Employee.pdf');
    },
    onError: async (error) => {
      getBlobErrorMessage(error).then(notifyError);
    },
  });
};
const printMasterSchedulerPlanner =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsPrintMasterSchedulePlannerGetRequest) =>
    api.saaSReportsReportApi.saaSReportsPrintMasterSchedulePlannerGet(body, {
      responseType: 'blob',
    });
export const usePrintMasterSchedulerPlanner = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(printMasterSchedulerPlanner(api), {
    onSuccess: async (response, requestBody) => {
      const data = get(response, 'data', '') as string;
      const blob = new Blob([data]);
      if (requestBody.format === 1) {
        saveAs(blob, 'SchedulePlanner.xlsx');
      } else {
        saveAs(blob, 'SchedulePlanner.pdf');
      }
    },
    onError: async (error) => {
      getBlobErrorMessage(error).then(notifyError);
    },
  });
};
const reportsPrintDashboardGet =
  (api: Api) =>
  (body: SaaSReportsReportApiSaaSReportsPrintDashboardGetRequest) =>
    api.saaSReportsReportApi.saaSReportsPrintDashboardGet(body, {
      responseType: 'blob',
    });
export const useReportsPrintDashboardGet = () => {
  const api = useApi();
  const { notifyError, notifyWarning } = useSnackBar();
  return useMutation(reportsPrintDashboardGet(api), {
    onSuccess: async (response, requestBody) => {
      if (response.status === 204) {
        notifyWarning('Report is empty!');
        return;
      }

      const data = get(response, 'data', '') as string;
      const excelBlob = new Blob([data]);
      saveAs(excelBlob, 'Dashboard Export.xlsx');
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
